// 📄 knownErrorsService.ts
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { uploadAttachments, UploadedAttachment } from './storageService.ts';

export interface KnownErrorModule {
  id: string;
  label: string;
}

// Lista abierta de módulos — agregar uno nuevo acá es todo lo que se
// necesita para que aparezca en el selector del formulario de alta.
export const KNOWN_ERROR_MODULES: KnownErrorModule[] = [
  { id: 'ariba', label: 'SAP Ariba' },
  { id: 'btp', label: 'SAP BTP' },
  { id: 'erp', label: 'ERP' }
];

export interface KnownErrorSolucionOption {
  id: string;
  label: string;
}

// Submenú "Solución / Canal" condicionado al módulo elegido (ver
// KnownErrorFormModal.tsx). Campo opcional — un módulo sin entrada acá
// simplemente no muestra el segundo dropdown.
export const KNOWN_ERROR_SOLUCIONES_POR_MODULO: Record<string, KnownErrorSolucionOption[]> = {
  ariba: [
    { id: 'commerce', label: 'Commerce Automation' },
    { id: 'slp', label: 'SLP (Supplier Lifecycle & Performance)' },
    { id: 'sourcing', label: 'Sourcing' },
    { id: 'buying', label: 'Buying (Compras)' },
    { id: 'contracts', label: 'Contracts' },
    { id: 'cig', label: 'CIG (Cloud Integration Gateway)' },
    { id: 'general', label: 'General / Transversal' }
  ],
  btp: [
    { id: 'integration-suite', label: 'Integration Suite (iFlows)' },
    { id: 'cis', label: 'Cloud Identity Service (IAS/IPS)' },
    { id: 'mdi', label: 'Master Data Integration' },
    { id: 'sap-build', label: 'SAP Build' },
    { id: 'hana-cloud', label: 'HANA Cloud' },
    { id: 'analytics-cloud', label: 'Analytics Cloud' },
    { id: 'work-zone', label: 'Work Zone' },
    { id: 'general', label: 'General / Transversal' }
  ],
  erp: [
    { id: 'oc', label: 'Órdenes de Compra (OC)' },
    { id: 'facturas', label: 'Facturas' },
    { id: 'pagos', label: 'Pagos' },
    { id: 'rfq', label: 'RFQ / Licitaciones' },
    { id: 'hes', label: 'HES / Confirmaciones' },
    { id: 'expedicion', label: 'Expedición' },
    { id: 'general', label: 'General / Transversal' }
  ]
};

export interface SistemaAfectadoMeta {
  id: string;
  label: string;
}

// Alcance/plataforma afectada — independiente de `module`/`solucion`
// (esos siguen siendo la clasificación interna del Admin). Este campo es
// el que se usa para filtrar en el catálogo público, ahora que "Errores
// Conocidos" dejó de estar acotado a la página de un solo módulo.
export const SISTEMAS_AFECTADOS: SistemaAfectadoMeta[] = [
  { id: 'ariba', label: 'SAP Ariba' },
  { id: 'erp', label: 'ERP (ECC/R3)' },
  { id: 's4hana', label: 'S/4HANA' },
  { id: 'btp-cig', label: 'SAP BTP / CIG' },
  { id: 'todos', label: 'Todos' }
];

export const sistemaAfectadoMeta = (id?: string): SistemaAfectadoMeta | null =>
  SISTEMAS_AFECTADOS.find(s => s.id === id) || null;

// Unión (deduplicada por id) de las soluciones/canales de todos los
// módulos — usada por el filtro "Solución" del catálogo público, que ya
// no está acotado a un solo módulo (ver KnownErrorsCatalogToolbox.tsx).
export const ALL_KNOWN_ERROR_SOLUCIONES: KnownErrorSolucionOption[] = Object.values(KNOWN_ERROR_SOLUCIONES_POR_MODULO)
  .flat()
  .reduce<KnownErrorSolucionOption[]>((acc, s) => (acc.some(o => o.id === s.id) ? acc : [...acc, s]), []);

// Diagnóstico automático de la extensión de Chrome (D1 de
// docs/plans/plan-diagnostico-documentos.md): con qué eventos del Document
// History de Business Network se reconoce este error. Un patrón se cumple si
// UN mismo evento cumple todas sus condiciones (código igual y/o texto
// contenido); el error coincide si se cumple alguno de sus patrones. Lo
// evalúa functions/services/documentDiagnosisService.js.
export interface KnownErrorPatron {
  codigo: string;
  texto: string;
}

export const TIPOS_DOCUMENTO_BN: { id: string; label: string }[] = [
  { id: 'invoice', label: 'Factura' }
];

// Vacío = aplica a cualquier estatus.
export const ESTATUS_DOCUMENTO_BN: { id: string; label: string }[] = [
  { id: 'rejected', label: 'Rejected' },
  { id: 'failed', label: 'Failed' },
  { id: 'canceled', label: 'Canceled' },
  { id: 'sent', label: 'Sent' }
];

export const normalizarPatrones = (patrones: KnownErrorPatron[] = []): KnownErrorPatron[] =>
  patrones
    .map(p => ({ codigo: (p.codigo || '').trim().toUpperCase(), texto: (p.texto || '').trim() }))
    .filter(p => p.codigo || p.texto);

// Códigos de los patrones que no llevan texto: coinciden con cualquier evento
// de ese código, sea cual sea la causa (DOC-197 sirve para fallas muy distintas).
export const codigosSinTexto = (patrones: KnownErrorPatron[] = []): string[] =>
  [...new Set(normalizarPatrones(patrones).filter(p => p.codigo && !p.texto).map(p => p.codigo))];

export interface KnownErrorFormData {
  code: string;
  title: string;
  module: string;
  solucion?: string;
  summary: string;
  cause: string;
  solution: string;
  tags: string[];
  sistemaAfectado?: string;
  versionAriba?: string;
  notaSapUrl?: string;
  fechaVerificacion?: Date | null;
  activo: boolean;
  tiposDocumento?: string[];
  estatusAplica?: string[];
  patrones?: KnownErrorPatron[];
}

export interface KnownError extends Omit<KnownErrorFormData, 'fechaVerificacion'> {
  id: string;
  attachments: UploadedAttachment[];
  source: 'manual' | 'auto';
  createdBy: string;
  createdAt?: any;
  updatedAt?: any;
  // Firestore Timestamp (con `.toDate()`), no un `Date` — el formulario
  // manda un `Date` al guardar, pero al leer siempre vuelve como Timestamp.
  fechaVerificacion?: any;
}

// El admin puede pegar el número de la nota (ej. 3749732) o la URL
// completa — si ya es una URL se usa tal cual, si es solo el número se
// arma el link al portal oficial de SAP (a diferencia del campo análogo
// de Mi Espacio > Notas SAP, que apunta a support.ariba.com: acá se pidió
// específicamente me.sap.com).
export const resolveNotaSapUrl = (valor?: string): string | null => {
  const trimmed = (valor || '').trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://me.sap.com/notes/${trimmed}`;
};

export const getKnownErrors = async (module: string): Promise<KnownError[]> => {
  const q = query(collection(db, "known_errors"), where("module", "==", module));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<KnownError, 'id'>) }));
};

// Sin filtro de módulo — usado por el panel Admin para listar el catálogo
// completo (la vista pública sigue usando getKnownErrors(module), que ya
// filtra por el contexto de la sección donde vive).
export const getAllKnownErrors = async (): Promise<KnownError[]> => {
  const snap = await getDocs(collection(db, "known_errors"));
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<KnownError, 'id'>) }));
};

/**
 * Crea un error nuevo: genera el ID primero, sube los adjuntos a
 * Storage bajo ese ID, y luego guarda el documento con las URLs.
 */
export const createKnownError = async (
  data: KnownErrorFormData,
  files: File[],
  createdBy: string
): Promise<string> => {
  if (!data.code.trim() || !data.title.trim()) {
    throw new Error("El código y el título del error son obligatorios.");
  }

  const ref = doc(collection(db, "known_errors"));
  const attachments = files.length > 0
    ? await uploadAttachments(`known_errors/${ref.id}`, files)
    : [];

  await setDoc(ref, {
    code: data.code.trim(),
    title: data.title.trim(),
    module: data.module,
    solucion: (data.solucion || '').trim(),
    summary: data.summary.trim(),
    cause: data.cause.trim(),
    solution: data.solution.trim(),
    tags: data.tags.map(t => t.trim().toLowerCase()).filter(Boolean),
    sistemaAfectado: data.sistemaAfectado || '',
    versionAriba: (data.versionAriba || '').trim(),
    notaSapUrl: (data.notaSapUrl || '').trim(),
    fechaVerificacion: data.fechaVerificacion || null,
    activo: data.activo,
    tiposDocumento: data.tiposDocumento || [],
    estatusAplica: data.estatusAplica || [],
    patrones: normalizarPatrones(data.patrones),
    attachments,
    source: 'manual',
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return ref.id;
};

/**
 * Actualiza un error existente. `keepAttachments` son los adjuntos ya
 * subidos que el usuario decidió conservar (pudo haber quitado alguno en
 * el form); `newFiles` son los que se agregan en esta edición — se suben
 * y se concatenan a los que se conservan.
 */
export const updateKnownError = async (
  errorId: string,
  data: KnownErrorFormData,
  keepAttachments: UploadedAttachment[],
  newFiles: File[]
): Promise<void> => {
  if (!data.code.trim() || !data.title.trim()) {
    throw new Error("El código y el título del error son obligatorios.");
  }

  const uploaded = newFiles.length > 0
    ? await uploadAttachments(`known_errors/${errorId}`, newFiles)
    : [];

  await updateDoc(doc(db, "known_errors", errorId), {
    code: data.code.trim(),
    title: data.title.trim(),
    module: data.module,
    solucion: (data.solucion || '').trim(),
    summary: data.summary.trim(),
    cause: data.cause.trim(),
    solution: data.solution.trim(),
    tags: data.tags.map(t => t.trim().toLowerCase()).filter(Boolean),
    sistemaAfectado: data.sistemaAfectado || '',
    versionAriba: (data.versionAriba || '').trim(),
    notaSapUrl: (data.notaSapUrl || '').trim(),
    fechaVerificacion: data.fechaVerificacion || null,
    activo: data.activo,
    tiposDocumento: data.tiposDocumento || [],
    estatusAplica: data.estatusAplica || [],
    patrones: normalizarPatrones(data.patrones),
    attachments: [...keepAttachments, ...uploaded],
    updatedAt: serverTimestamp()
  });
};

export const deleteKnownError = async (errorId: string): Promise<void> => {
  await deleteDoc(doc(db, "known_errors", errorId));
};

export const matchesSearch = (error: KnownError, term: string): boolean => {
  if (!term.trim()) return true;
  const needle = term.trim().toLowerCase();
  return (
    error.code.toLowerCase().includes(needle) ||
    error.title.toLowerCase().includes(needle) ||
    error.summary.toLowerCase().includes(needle) ||
    error.cause.toLowerCase().includes(needle) ||
    error.solution.toLowerCase().includes(needle) ||
    error.tags.some(tag => tag.includes(needle))
  );
};

// Registros creados antes de agregar `activo` no tienen el campo — se
// tratan como activos (el default declarado en el formulario) en vez de
// desaparecer del catálogo público.
export const isKnownErrorActivo = (error: KnownError): boolean => error.activo !== false;
